import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError, type GroupDrawPreview } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { isStructuralDrawBlocker } from "../lib/groupDraw";
import { DrawCeremonyResult } from "./DrawCeremonyCard";

/** Sorteo público de grupos de Round Robin en el paso Grupos (Eduardo
 * 2026-09-26): vista previa (sembrados → grupo por serpentina, S/R que se
 * sortean), botón para sortear una sola vez y el acta cuando ya se hizo. */
export default function GroupDrawCard({
  token,
  tournamentId,
  divisionId,
  divisionName,
  refreshKey,
  onDrawn,
  onStateChange,
}: {
  token: string;
  tournamentId: number;
  divisionId: number | null;
  divisionName: string | null;
  /** Cambia cuando los grupos cambian (para refrescar la vista previa). */
  refreshKey?: string | number;
  /** Tras sortear: el padre recarga los grupos. */
  onDrawn: () => void | Promise<void>;
  /** ¿La categoría ya está sorteada? (el padre avisa al mover jugadores). */
  onStateChange?: (drawn: boolean) => void;
}) {
  const { askConfirm } = useAbortableConfirm([tournamentId, divisionId]);
  const [preview, setPreview] = useState<GroupDrawPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Solo la respuesta MÁS NUEVA pinta (al cambiar de categoría, una vista
  // previa vieja no debe aparecer bajo la categoría nueva).
  const requestSeqRef = useRef(0);
  // Categoría vigente (para no pintar ni recargar con la de un clic viejo).
  const divisionRef = useRef(divisionId);

  const load = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    try {
      const response = await api.groups.drawPreview(token, tournamentId, divisionId);
      if (seq !== requestSeqRef.current) return;
      setPreview(response.data);
      setError(null);
      onStateChange?.(Boolean(response.data.ceremony?.performed_at));
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      // Un error al refrescar NO dice "no sorteada": se conserva lo último que
      // se supo (si no, "Repartir desde cero" se reabría en una categoría ya
      // sorteada).
      setError(err instanceof ApiError ? err.message : "No se pudo cargar el sorteo de grupos.");
    }
    // onStateChange puede cambiar de identidad en cada render del padre: no es dependencia.
  }, [token, tournamentId, divisionId]);

  // Al cambiar de categoría no se queda en pantalla la anterior.
  useEffect(() => {
    divisionRef.current = divisionId;
    requestSeqRef.current += 1;
    setPreview(null);
    setError(null);
    setBusy(false);
  }, [tournamentId, divisionId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function handleDraw() {
    if (!preview || !preview.can_draw) return;
    const scope = divisionName ? `de ${divisionName}` : "del torneo";
    const reshuffleNote =
      preview.already_assigned_count > 0
        ? ` Los ${preview.already_assigned_count} jugador(es) que ya tienen grupo se reacomodan según el sorteo.`
        : "";
    const confirmed = await askConfirm(
      `¿Hacer el sorteo público de grupos ${scope}?\n\n` +
        `Sembrados en serpentina y los ${preview.unseeded.length} jugador(es) sin seed por sorteo.${reshuffleNote} ` +
        "El sorteo NO se puede repetir. Queda un acta pública; los jugadores la ven (y reciben el aviso) cuando el torneo esté publicado y con su Draw generado.",
      { acceptLabel: "Sí, hacer el sorteo" },
    );
    if (!confirmed) return;
    const divisionAtClick = divisionId;
    if (divisionRef.current !== divisionAtClick) return;
    setBusy(true);
    setError(null);
    try {
      await api.groups.draw(token, tournamentId, divisionAtClick);
      // Si el admin cambió de categoría mientras se sorteaba, la pantalla ya
      // recarga la nueva: no se pinta ni se recarga con la vieja.
      if (divisionRef.current !== divisionAtClick) return;
      await load();
      await onDrawn();
    } catch (err) {
      if (divisionRef.current !== divisionAtClick) return;
      setError(err instanceof ApiError ? err.message : "No se pudo hacer el sorteo.");
    } finally {
      if (divisionRef.current === divisionAtClick) setBusy(false);
    }
  }

  if (!preview) {
    return error ? (
      <section className="card group-draw-card" data-no-toast>
        <p className="form-error">{error}</p>
      </section>
    ) : null;
  }
  // No aplica (no es RR, grupo único, ya hay partidos, por equipos): no se
  // muestra; el estado ya se informó al padre.
  if (!preview.ceremony && preview.blockers.some(isStructuralDrawBlocker)) {
    return null;
  }

  const ceremony = preview.ceremony;
  return (
    <section className="card group-draw-card" data-no-toast>
      <p className="card__eyebrow">{divisionName ?? "Torneo"}</p>
      <h2>Sorteo público de grupos</h2>
      {ceremony ? (
        <>
          <DrawCeremonyResult ceremony={ceremony} />
          <p className="muted">
            Mover jugadores después del sorteo se permite; cada cambio queda registrado en el acta pública.
          </p>
        </>
      ) : (
        <>
          <p className="muted">{preview.rule}</p>
          {preview.seeded.length > 0 ? (
            <div className="responsive-table-wrap">
              <table className="data-table group-draw-card__table">
                <thead>
                  <tr>
                    <th>Seed</th>
                    <th>Jugador</th>
                    <th>Grupo (serpentina)</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.seeded.map((row) => (
                    <tr key={row.participant_id}>
                      <td>#{row.seed}</td>
                      <td>{row.display_name}</td>
                      <td>{row.group_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Nadie tiene seed: todos entran por sorteo.</p>
          )}
          <p className="muted">
            {preview.unseeded.length} jugador(es) sin seed (S/R) se sortean al azar hacia los grupos con menos jugadores.
            {preview.already_assigned_count > 0
              ? ` El sorteo reacomoda a los ${preview.already_assigned_count} que ya tienen grupo.`
              : ""}
          </p>
          {preview.blockers.length > 0 ? (
            <ul className="group-draw-card__blockers">
              {preview.blockers.map((reason) => (
                <li key={reason} className="muted">{reason}</li>
              ))}
            </ul>
          ) : null}
          <div className="form__actions">
            <button type="button" className="btn" onClick={() => void handleDraw()} disabled={busy || !preview.can_draw}>
              {busy ? "Sorteando…" : "Hacer el sorteo de grupos"}
            </button>
          </div>
        </>
      )}
      {error ? <p className="form-error" role="alert">{error}</p> : null}
    </section>
  );
}
